import { useCallback, useState } from 'react'
import { getUserSetting, saveUserSetting } from '@/utils/localStorage'
import type {
  CommandBarActionId,
  CommandBarEntityType,
  CommandBarRecentEntry,
} from '../types'

const RECENTS_KEY = 'nabu.commandBar.recents'
const MAX_RECENTS = 6

function readRecents(): CommandBarRecentEntry[] {
  const stored = getUserSetting(RECENTS_KEY)
  if (!Array.isArray(stored)) return []
  return stored.filter(
    (entry): entry is CommandBarRecentEntry =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof entry.id === 'string' &&
      typeof entry.label === 'string' &&
      typeof entry.timestamp === 'number'
  )
}

export function useCommandBarRecents() {
  const [recents, setRecents] = useState<CommandBarRecentEntry[]>(readRecents)

  const addRecent = useCallback(
    (entry: Omit<CommandBarRecentEntry, 'timestamp'>) => {
      setRecents((prev) => {
        const dedupeKey =
          entry.actionId != null
            ? `action:${entry.actionId}`
            : `${entry.type}:${entry.id}`

        const next: CommandBarRecentEntry[] = [
          { ...entry, timestamp: Date.now() },
          ...prev.filter((item) => {
            const itemKey =
              item.actionId != null
                ? `action:${item.actionId}`
                : `${item.type}:${item.id}`
            return itemKey !== dedupeKey
          }),
        ].slice(0, MAX_RECENTS)

        saveUserSetting(RECENTS_KEY, next)
        return next
      })
    },
    []
  )

  return { recents, addRecent }
}

export type AddRecentPayload = {
  id: string
  type: CommandBarEntityType | 'action'
  label: string
  subtitle?: string
  path?: string
  actionId?: CommandBarActionId
}
